import React, { useCallback, FC } from 'react';

import Select from '../../../../../../../atoms/Select';
import LabeledContainer from '../../../../../../../molecules/common/LabeledContainer';
import CheckInput, { InputType } from '../../../../../../../atoms/CheckInput';
import TextInput, { TextInputType } from '../../../../../../../atoms/TextInput';
import * as IBooks from '../../../../../../../interfaces/merchants/resources/books';
import { InputValueType } from '../..';
import ErrorMessage from '../../../../../../../molecules/merchant/resources/book/InputUserInfoErrorMessage/InputUserInfoErrorMessage';
import * as IInputs from '../../../../../../../interfaces/merchants/resources/books/inputs';

type ResponsibleElement =
  | HTMLInputElement
  | HTMLTextAreaElement
  | HTMLSelectElement;

interface ChildProps {
  onChange: (e: React.ChangeEvent<ResponsibleElement>) => void;
  values: IInputs.QuestionnaireValueType;
}

interface TextProps extends ChildProps {
  questionnaire: IBooks.QuestionnaireText;
}

interface TextAreaProps extends ChildProps {
  questionnaire: IBooks.QuestionnaireTextArea;
}

interface SelectProps extends ChildProps {
  questionnaire: IBooks.QuestionnaireSelect;
}

interface RadioProps extends ChildProps {
  questionnaire: IBooks.QuestionnaireRadio;
}

interface CheckboxsProps extends ChildProps {
  questionnaire: IBooks.QuestionnaireCheckbox;
}

interface CheckboxProps extends ChildProps {
  option: { label: string; value: string };
  customFieldId: number;
}

export const questionnairesSubmittable = (args: {
  questionnaires?: IBooks.Questionnaire[];
  values?: IInputs.QuestionnaireValueType;
  isFirstTimeReservation: boolean;
}): boolean => {
  const { questionnaires, values, isFirstTimeReservation } = args;
  if (!questionnaires) {
    return true;
  }
  if (isFirstTimeReservation) {
    const requiredQuestionnaires = questionnaires.filter((q) => q.required);
    return validateQuestionnairesSubmittable({
      requiredQuestionnaires,
      values,
    });
  } else {
    const requiredQuestionnaires = questionnaires.filter(
      (q) => q.required && !q.firstTimeOnly,
    );
    return validateQuestionnairesSubmittable({
      requiredQuestionnaires,
      values,
    });
  }
};

const validateQuestionnairesSubmittable = (args: {
  requiredQuestionnaires: IBooks.Questionnaire[];
  values?: IInputs.QuestionnaireValueType;
}) => {
  const { requiredQuestionnaires, values } = args;
  if (requiredQuestionnaires.length === 0) {
    return true;
  }
  if (!values) {
    return false;
  }
  return requiredQuestionnaires.every((q) => {
    const v = values[q.customFieldId];
    switch (typeof v) {
      case 'string':
        return !!v;
      case 'undefined':
        return false;
      case 'object': // questionnaire checkboxの場合
        return Object.values(v as { [v: string]: boolean }).some((b) => b);
      default:
        throw new Error(`something wrong with ${v}`);
    }
  });
};

const Text: FC<TextProps> = ({ questionnaire, onChange, values }) => (
  <TextInput
    type={TextInputType.Text}
    name={`${questionnaire.customFieldId}`}
    value={(values[questionnaire.customFieldId] as string) || ''}
    onChange={onChange}
  />
);

const TextArea: FC<TextAreaProps> = ({ questionnaire, onChange, values }) => (
  <TextInput
    type={TextInputType.TextArea}
    rows={6}
    name={`${questionnaire.customFieldId}`}
    value={(values[questionnaire.customFieldId] as string) || ''}
    onChange={onChange}
  />
);

const SelectBox: FC<SelectProps> = ({ questionnaire, onChange, values }) => {
  return (
    <Select
      name={`${questionnaire.customFieldId}`}
      value={(values[questionnaire.customFieldId] as string) || ''}
      onChange={onChange}
      selected={(values[questionnaire.customFieldId] as string) !== undefined}
    >
      {questionnaire.options.map((o, i) => (
        <option key={i} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  );
};

const Radio: FC<RadioProps> = ({ questionnaire, onChange, values }) => {
  return (
    <div>
      {questionnaire.options.map((o, i) => (
        <CheckInput
          key={i}
          type={InputType.Radio}
          name={`${questionnaire.customFieldId}`}
          value={o.value}
          onChange={onChange}
          checked={
            ((values[questionnaire.customFieldId] as string) || '') === o.value
          }
        >
          {o.label}
        </CheckInput>
      ))}
    </div>
  );
};

const Checkbox: FC<CheckboxProps> = ({
  option,
  customFieldId,
  onChange,
  values,
}) => {
  const currentValues = values[customFieldId] || {};
  const checked = !!(
    typeof currentValues === 'object' && currentValues[option.value]
  );
  return (
    <CheckInput
      type={InputType.Checkbox}
      name={
        // 最終的に送るのはvalueのみでいいので
        // { value: boolean } の構造体でcheck状態を管理する
        //
        // 実際は少し複雑でこんな感じ
        // { 123: { 'a': true, 'b': false } }
        //
        // この構造体のうち{'b': false}を置き換えるため
        // 以下のような名前にする
        // 123.b
        `${customFieldId}.${option.value}`
      }
      value={option.value}
      onChange={onChange}
      checked={checked}
    >
      {option.label}
    </CheckInput>
  );
};

const Checkboxs: FC<CheckboxsProps> = ({ questionnaire, onChange, values }) => {
  return (
    <>
      {questionnaire.options.map((o, i) => (
        <Checkbox
          key={i}
          customFieldId={questionnaire.customFieldId}
          option={o}
          onChange={onChange}
          values={values}
        />
      ))}
    </>
  );
};

const isText = (q: IBooks.Questionnaire): q is IBooks.QuestionnaireText =>
  q.type === 'text';

const isTextArea = (
  q: IBooks.Questionnaire,
): q is IBooks.QuestionnaireTextArea => q.type === 'text_area';

const isSelect = (q: IBooks.Questionnaire): q is IBooks.QuestionnaireSelect =>
  q.type === 'select';

const isRadio = (q: IBooks.Questionnaire): q is IBooks.QuestionnaireRadio =>
  q.type === 'radio';

const isCheckbox = (
  q: IBooks.Questionnaire,
): q is IBooks.QuestionnaireCheckbox => q.type === 'checkbox';

interface Props {
  questionnaires: IBooks.Questionnaire[];
  onChange: (name: string, value: InputValueType) => void;
  namePrefix: 'questionnaires';
  values: IInputs.QuestionnaireValueType;
  currentEmail?: string;
  isFirstTimeReservation: boolean;
}

const InputUserInfoQuestionnaires: FC<Props> = (props) => {
  const { namePrefix, onChange, isFirstTimeReservation } = props;

  const handleChange = useCallback(
    (e: React.ChangeEvent<ResponsibleElement>) => {
      const {
        currentTarget: { name, value, type },
      } = e;

      if (type === 'checkbox') {
        const {
          currentTarget: { checked },
        } = e as React.ChangeEvent<HTMLInputElement>;
        onChange(`${namePrefix}.${name}`, checked);
      } else {
        onChange(`${namePrefix}.${name}`, value);
      }
    },
    [namePrefix, onChange],
  );

  const renderQuestionnaire = useCallback(
    (questionnaire: IBooks.Questionnaire) => {
      if (isText(questionnaire)) {
        return (
          <Text
            {...props}
            key={questionnaire.customFieldId}
            questionnaire={questionnaire}
            onChange={handleChange}
          />
        );
      }
      if (isTextArea(questionnaire)) {
        return (
          <TextArea
            {...props}
            key={questionnaire.customFieldId}
            questionnaire={questionnaire}
            onChange={handleChange}
          />
        );
      }
      if (isSelect(questionnaire)) {
        return (
          <SelectBox
            {...props}
            key={questionnaire.customFieldId}
            questionnaire={questionnaire}
            onChange={handleChange}
          />
        );
      }
      if (isRadio(questionnaire)) {
        return (
          <Radio
            {...props}
            key={questionnaire.customFieldId}
            questionnaire={questionnaire}
            onChange={handleChange}
          />
        );
      }
      if (isCheckbox(questionnaire)) {
        return (
          <Checkboxs
            {...props}
            key={questionnaire.customFieldId}
            questionnaire={questionnaire}
            onChange={handleChange}
          />
        );
      }
      throw new Error();
    },
    [props, handleChange],
  );
  const { questionnaires } = props;

  return (
    <>
      {questionnaires.map((q) => {
        if (q.firstTimeOnly && !isFirstTimeReservation) {
          return null;
        } else {
          return (
            <div key={q.customFieldId}>
              <LabeledContainer
                label={q.label}
                requiredLabel={q.required}
                paddingTop={true}
                errorStyle={q.errors && q.errors.length !== 0}
              >
                {renderQuestionnaire(q)}
              </LabeledContainer>
              <ErrorMessage fieldSource={q} />
            </div>
          );
        }
      })}
    </>
  );
};

export default InputUserInfoQuestionnaires;
